import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TENANT_PERMISSIONS, TENANT_ROLE_PRESETS, type TenantPermission } from '@loctopus/rbac';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate } from '../../lib/format';
import { useAuth } from '../../lib/auth';
import type { ClientUser, Role } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  HelpPanel,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Tabs,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

export function StaffPage() {
  const [tab, setTab] = useState('staff');

  return (
    <div>
      <PageHeader
        title="Staff & roles"
        description="Manage who on your team can access the dashboard, and what they can do."
      />
      <HelpPanel storageKey="help-staff-roles" title="What is this?">
        This is where you invite the people who work for you and control what each of them can
        do in this dashboard - for example, letting a box office staff member check in tickets
        without also giving them access to refunds or your payout details.
      </HelpPanel>
      <Tabs
        tabs={[
          { key: 'staff', label: 'Staff' },
          { key: 'roles', label: 'Custom roles' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'staff' && <StaffList />}
      {tab === 'roles' && <RolesList />}
    </div>
  );
}

function StaffList() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editRoleTarget, setEditRoleTarget] = useState<ClientUser | null>(null);
  const [resetTotpTarget, setResetTotpTarget] = useState<ClientUser | null>(null);
  const [actionError, setActionError] = useState<ParsedApiError | null>(null);
  const queryClient = useQueryClient();
  const { email: ownEmail } = useAuth();
  const staffQuery = useQuery({ queryKey: ['staff'], queryFn: api.staff.list });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      api.staff.setStatus(id, status),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (err) => setActionError(toParsedError(err)),
  });

  const resetTotpMutation = useMutation({
    mutationFn: (id: string) => api.staff.resetTotp(id),
    onSuccess: (result) => {
      setActionError(null);
      setResetTotpTarget(null);
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      window.alert(result.message);
    },
    onError: (err) => setActionError(toParsedError(err)),
  });

  return (
    <Card
      title="Staff"
      actions={
        <RequirePermission permission="staff.manage">
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            Invite staff
          </Button>
        </RequirePermission>
      }
    >
      {actionError && <ErrorSummary error={actionError} className="mb-3" />}
      {staffQuery.isLoading ? (
        <Spinner />
      ) : staffQuery.error ? (
        <ErrorSummary error={toParsedError(staffQuery.error)} />
      ) : (
        <DataTable<ClientUser>
          columns={[
            { key: 'email', header: 'Email', render: (u) => u.email },
            { key: 'roles', header: 'Roles', render: (u) => u.roleNames?.join(', ') ?? '-' },
            {
              key: 'invite',
              header: 'Invite',
              render: (u) =>
                u.inviteStatus === 'pending' ? (
                  <Badge tone="warning">Pending</Badge>
                ) : (
                  <Badge tone="success">Accepted</Badge>
                ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (u) => (
                <Badge tone={u.status === 'active' ? 'success' : 'neutral'}>{u.status}</Badge>
              ),
            },
            {
              key: '2fa',
              header: '2FA',
              render: (u) =>
                u.totpEnabled ? (
                  <Badge tone="success">Enabled</Badge>
                ) : (
                  <Badge tone="warning">Not enabled</Badge>
                ),
            },
            { key: 'lastLogin', header: 'Last login', render: (u) => formatDate(u.lastLoginAt) },
            {
              key: 'actions',
              header: '',
              render: (u) => (
                <RequirePermission permission="staff.manage">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditRoleTarget(u)}>
                      Edit role
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={statusMutation.isPending || u.email === ownEmail}
                      onClick={() =>
                        statusMutation.mutate({
                          id: u.id,
                          status: u.status === 'active' ? 'disabled' : 'active',
                        })
                      }
                    >
                      {u.status === 'active' ? 'Disable' : 'Re-enable'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResetTotpTarget(u)}>
                      Reset 2FA
                    </Button>
                  </div>
                </RequirePermission>
              ),
            },
          ]}
          rows={staffQuery.data?.data ?? []}
          getRowKey={(u) => u.id}
          emptyMessage="No staff invited yet."
        />
      )}
      <InviteStaffModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <EditStaffRoleModal target={editRoleTarget} onClose={() => setEditRoleTarget(null)} />
      <Modal
        open={!!resetTotpTarget}
        onClose={() => setResetTotpTarget(null)}
        title="Reset 2FA"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResetTotpTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={resetTotpMutation.isPending}
              onClick={() => resetTotpTarget && resetTotpMutation.mutate(resetTotpTarget.id)}
            >
              {resetTotpMutation.isPending ? 'Resetting...' : 'Reset 2FA'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          This signs {resetTotpTarget?.email} out of every existing session and clears their
          two-factor authentication - they will be asked to set it up again (scan a new QR code)
          the next time they sign in. Use this when someone has lost their authenticator device or
          their account may be compromised.
        </p>
      </Modal>
    </Card>
  );
}

function InviteStaffModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: api.roles.list, enabled: open });

  const mutation = useMutation({
    mutationFn: () => api.staff.invite({ email, roleIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite staff"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!email.trim() || roleIds.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Inviting...' : 'Send invite'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Email" error={fieldError(formError, 'email')}>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </FormField>
      <FormField label="Roles">
        <div className="space-y-1">
          {rolesQuery.data?.data.map((role) => (
            <label key={role.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={roleIds.includes(role.id)}
                onChange={(e) =>
                  setRoleIds((prev) =>
                    e.target.checked ? [...prev, role.id] : prev.filter((id) => id !== role.id),
                  )
                }
              />
              {role.name} {role.isPreset && <Badge tone="info">preset</Badge>}
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}

function EditStaffRoleModal({
  target,
  onClose,
}: {
  target: ClientUser | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: api.roles.list, enabled: !!target });

  // Re-seed the checkbox selection from the target's CURRENT roles every time a different staff
  // member is opened (by id, not by name - see `ClientUser.roleIds`'s doc comment for why).
  useEffect(() => {
    if (target) setRoleIds(target.roleIds ?? []);
  }, [target]);

  const mutation = useMutation({
    mutationFn: () => api.staff.updateRole(target!.id, { roleIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={!!target}
      onClose={onClose}
      title={`Edit role - ${target?.email ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={roleIds.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save role'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Roles">
        <div className="space-y-1">
          {rolesQuery.data?.data.map((role) => (
            <label key={role.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={roleIds.includes(role.id)}
                onChange={(e) =>
                  setRoleIds((prev) =>
                    e.target.checked ? [...prev, role.id] : prev.filter((id) => id !== role.id),
                  )
                }
              />
              {role.name} {role.isPreset && <Badge tone="info">preset</Badge>}
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}

function RolesList() {
  const [createOpen, setCreateOpen] = useState(false);
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: api.roles.list });

  const presetRows = Object.values(TENANT_ROLE_PRESETS);

  return (
    <div className="space-y-6">
      <Card title="Preset roles">
        <DataTable
          columns={[
            { key: 'name', header: 'Name', render: (r: (typeof presetRows)[number]) => r.name },
            {
              key: 'permissions',
              header: 'Permissions',
              render: (r: (typeof presetRows)[number]) =>
                `${r.permissions.length} / ${TENANT_PERMISSIONS.length}`,
            },
          ]}
          rows={presetRows}
          getRowKey={(r) => r.name}
          emptyMessage="No presets."
        />
      </Card>
      <Card
        title="Custom roles"
        actions={
          <RequirePermission permission="roles.manage">
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              New custom role
            </Button>
          </RequirePermission>
        }
      >
        {rolesQuery.isLoading ? (
          <Spinner />
        ) : rolesQuery.error ? (
          <ErrorSummary error={toParsedError(rolesQuery.error)} />
        ) : (
          <DataTable<Role>
            columns={[
              { key: 'name', header: 'Name', render: (r) => r.name },
              {
                key: 'permissions',
                header: 'Permissions',
                render: (r) => `${r.permissions.length} / ${TENANT_PERMISSIONS.length}`,
              },
              { key: 'createdAt', header: 'Created', render: (r) => formatDate(r.createdAt) },
            ]}
            rows={(rolesQuery.data?.data ?? []).filter((r) => !r.isPreset)}
            getRowKey={(r) => r.id}
            emptyMessage="No custom roles yet - every tenant starts with the preset roles above."
          />
        )}
      </Card>
      <CreateRoleModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function CreateRoleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState<TenantPermission[]>([]);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.roles.create({ name, permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
      setName('');
      setPermissions([]);
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  function toggle(p: TenantPermission) {
    setPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="New custom role"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || permissions.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create role'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Role name" error={fieldError(formError, 'name')}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Box Office Lead"
        />
      </FormField>
      <FormField
        label="Permissions"
        hint="Checked here is a UI courtesy only - apps/api re-checks every permission server-side, and per RBAC.md an admin can only grant permissions they themselves hold."
      >
        <div className="grid grid-cols-2 gap-1 max-h-72 overflow-y-auto border border-slate-200 rounded-brand p-3">
          {TENANT_PERMISSIONS.map((p) => (
            <label key={p} className="flex items-center gap-2 text-sm py-0.5">
              <input type="checkbox" checked={permissions.includes(p)} onChange={() => toggle(p)} />
              <span className="font-mono text-xs">{p}</span>
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}
